import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { AlphaEyeKeyLevelCategory } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';

/** Stable, presentation-ready row for the Key Levels ladder. */
export type KeyLevelLadderRow =
    | {
          kind: 'level';
          /** Stable id assigned at projection time; safe for keyed `{#each}`. */
          id: string;
          name: string;
          category: AlphaEyeKeyLevelCategory;
          /** Live indicator value. The model filters out rows where this isn't finite. */
          price: number;
          /**
           * Signed dollar delta vs the current LAST price (`price - last`). UI
           * renders with a forced sign and green/red tint based on sign. `null`
           * only when LAST is unavailable.
           */
          delta: number | null;
          /**
           * Optional percent delta `(price - last) / last`. `null` when LAST
           * is missing or zero. Exposed alongside `delta` for any view that
           * wants `+1.10%` instead of `+2.26`.
           */
          deltaPercent: number | null;
          /**
           * Classification vs the current LAST. Rows where `price > last` are
           * `resistance` (UI renders with red tint + border-left); rows where
           * `price <= last` are `support` (blue tint + border-left). When LAST
           * is unavailable, everything classifies as `support`.
           */
          classification: 'support' | 'resistance';
      }
    | {
          kind: 'current';
          /** The live LAST price; inserted at the rung where it sits in the sorted ladder. */
          price: number;
      };

/** Stable projection of a config row — id is per-instance, persisted across re-emits via WeakMap. */
export type ConfigRow =
    | {
          readonly kind: 'indicator';
          readonly id: string;
          readonly name: string;
          readonly category: AlphaEyeKeyLevelCategory;
          readonly indicatorId: string;
      }
    | {
          readonly kind: 'formula';
          readonly id: string;
          readonly name: string;
          readonly category: AlphaEyeKeyLevelCategory;
          readonly formula: string;
          readonly imports: readonly IndicatorImportRef[];
      };

export interface KeyLevelsState {
    /** `null` → "no filter applied" (UI shows all chips active). */
    readonly activeCategories: ReadonlySet<AlphaEyeKeyLevelCategory> | null;
}

const DEFAULT_ACTIVE_CATEGORY_VALUES = ['highs-lows', 'ohlc'] as const satisfies readonly AlphaEyeKeyLevelCategory[];

export function createDefaultActiveCategories(): ReadonlySet<AlphaEyeKeyLevelCategory> {
    return new Set<AlphaEyeKeyLevelCategory>(DEFAULT_ACTIVE_CATEGORY_VALUES);
}

export function createInitialState(overrides?: Partial<KeyLevelsState>): KeyLevelsState {
    return {
        activeCategories: createDefaultActiveCategories(),
        ...overrides,
    };
}
